import { NoteContent } from '@elsevier-health/osmosis-shared/types/learndb/learnData';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { H3, P } from '@osmosis/tailwind-ui/components/Text';
import { bg0, bg200 } from '@osmosis/tailwind-ui/styles';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { useIntl, FormattedMessage } from 'react-intl';

interface NotesPageContentsBlockProps {
  contents?: NoteContent[];
  selectedPage: number;
  onSelectPage: (page: number) => void;
}

/**
 * Renders a block displaying a list of note contents.
 * @param {NotesPageContentsBlockProps} props - The props for the component.
 */
const NotePageContentsBlock = ({
  contents,
  selectedPage,
  onSelectPage,
}: NotesPageContentsBlockProps) => {
  const intl = useIntl();
  if (!contents || contents.length === 0) {
    return null;
  }

  const sortedContents = contents.sort((a, b) => +a.pageNum - +b.pageNum);

  const getButtonLabel = (title: string) => {
    return intl.formatMessage(
      {
        id: 'notesPage.contents.pages.jumpTo',
        defaultMessage: 'jump-to-{title}-page',
      },
      { title: title.replace(/\s/g, '-') },
    );
  };

  return (
    <Box className={`my-2 p-4 ${bg0} rounded-md`}>
      <H3 className="border-b-[2px] pb-3">
        <FormattedMessage id="notesPage.contents" defaultMessage="Contents" />
      </H3>
      <Box className="mt-2">
        {sortedContents.map((note, index) => {
          const currentSection = +note.pageNum;
          // no i+1 means that is the last section, so anything above it is active
          const nextSection = sortedContents[index + 1]?.pageNum ?? Infinity;

          const isActive =
            selectedPage >= currentSection && selectedPage < +nextSection;

          return (
            <Button
              data-testid={`jump-to-${note.learnPage.replace(
                /\s/g,
                '-',
              )}-section`}
              key={index}
              variant="transparent"
              className={`mt-2 flex w-full justify-between rounded-md p-2 ${
                isActive ? bg200 : ''
              } `}
              onClick={() => onSelectPage(currentSection)}
              aria-label={getButtonLabel(note.learnPage)}
            >
              <P className={`${isActive ? 'font-bold' : ''} text-left`}>
                {note.learnPage}
              </P>
              {isActive && (
                <Icon className="self-center" icon={faChevronRight} />
              )}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default NotePageContentsBlock;
